import uniqBy from 'lodash/uniqBy';
import { z } from 'zod';

import { REPORT_CELL_MENTION_PATH } from '../types/mention-urls';

/**
 * Single source of truth for the mention types that travel over the
 * `mention:<type>/<id>` markdown wire format. The MentionUrl template-literal
 * union (see `types/mention-urls.ts`) and the membership guard below are both
 * derived from this tuple, so adding a type here propagates everywhere.
 *
 * `'selection-v2'` is the current production selection form; `'selection'`
 * (v1) is recognized only so historical chat history still decodes — no new
 * prompts produce it.
 *
 * `user` and `playbook` mentions are used in workflow task descriptions
 * assigned to Aline AI; they may appear in any markdown that flows through
 * the same chat-editor wire format.
 *
 * `playbook-rule` names one rule of a playbook by the rule's id, so a request
 * about a rule carries the rule itself rather than its title in quotes.
 */
export const MENTION_TYPES = [
  'document',
  'email',
  'intake-form',
  'party',
  'playbook',
  'playbook-rule',
  'report',
  'selection',
  'selection-v2',
  'selection-v3',
  'task',
  'user',
  'workflow',
] as const;

export type MentionType = (typeof MENTION_TYPES)[number];

const ALINE_AI_MENTION_PATH = 'aline-ai';

/**
 * Path prefix of a report-cell citation the analysis tools emit. The parser
 * recognises it only to skip it quietly instead of warning about an unknown
 * mention on every parse of a message that cites cells.
 */
const REPORT_CELL_MENTION_PATH_PREFIX = `${REPORT_CELL_MENTION_PATH}/`;
const MENTION_TYPE_SET: ReadonlySet<string> = new Set(MENTION_TYPES);

export function getIsMentionType(type: string): type is MentionType {
  return MENTION_TYPE_SET.has(type);
}

/**
 * Mention types whose id is a document id: a direct document mention, or a
 * selection (any version) within one. The single place to extend when a new
 * selection format arrives — every consumer that resolves mentions to
 * documents goes through {@link getIsDocumentBackedMentionType}, so a
 * `selection-v4` that is missing here fails to resolve everywhere at once,
 * loudly, instead of silently dropping documents at whichever inline
 * union was not updated.
 */
export const DOCUMENT_BACKED_MENTION_TYPES = [
  'document',
  'selection',
  'selection-v2',
  'selection-v3',
] as const satisfies readonly MentionType[];

export type DocumentBackedMentionType = (typeof DOCUMENT_BACKED_MENTION_TYPES)[number];

const DOCUMENT_BACKED_MENTION_TYPE_SET: ReadonlySet<MentionType> = new Set(
  DOCUMENT_BACKED_MENTION_TYPES
);

export function getIsDocumentBackedMentionType(
  type: MentionType
): type is DocumentBackedMentionType {
  return DOCUMENT_BACKED_MENTION_TYPE_SET.has(type);
}

/**
 * Utility for parsing file mention links from markdown text.
 *
 * Mention format: [@fileName](mention:mentionTypeName/fileId)
 * Also accepts [fileName](mention:mentionTypeName/fileId) from AI-authored output.
 * Optional query params: ?ctx=... (for selections)
 * Example: [@Contract.pdf](mention:document/abc-123)
 */
export interface ParsedMention {
  /**
   * The entity ID extracted from the mention URL.
   * - For documents: 'doc-123'
   * - For selections (v1, v2, v3): 'doc-123' (document ID only)
   * - For intake forms: 'current' (resolved from the active workflow/task context)
   * - For parties: 'party-456'
   * - For tasks: 'task-123'
   * - For workflows: 'wf-789'
   * - For reports: 'report-123'
   * - For playbook rules: 'rule-123'
   */
  id: string;
  /** The display name shown in the markdown link (e.g., 'Contract.pdf', 'John Doe', 'Approval Workflow') */
  name: string;
  /** The type of mention — see the MENTION_TYPES doc for type-specific notes. */
  type: MentionType;
}

const DOCUMENT_MENTION_ID_SCHEMA = z.uuid();

/**
 * Parses the path part of a mention URL to extract the type and ID.
 * For selections, extracts only the document ID.
 *
 * @param pathPart - The path portion before query params
 * @returns Parsed type and ID, or null if invalid
 */
function parsePathPart(pathPart: string): { id: string; type: MentionType } | null {
  // Format: 'document/id' or 'selection/doc-123/sel-1'
  const slashIndex = pathPart.indexOf('/');
  if (slashIndex === -1) return null;

  const type = pathPart.slice(0, slashIndex);
  const rawId = pathPart.slice(slashIndex + 1);
  if (!getIsMentionType(type)) return null;

  const id = type === 'email' ? decodeMentionEmailId(rawId) : rawId;

  /**
   * For selections (v1/v2/v3), extract only the document ID (before any
   * additional slashes): 'doc-123/sel-1' -> 'doc-123'. The short by-id form is
   * shared across selection types — see `transformMarkdownWithSelectionIds` on
   * the server.
   */
  if (getIsDocumentBackedMentionType(type) && type !== 'document') {
    const nextSlashIndex = id.indexOf('/');
    if (nextSlashIndex !== -1) return { id: id.slice(0, nextSlashIndex), type };
  }

  return { id, type };
}

function decodeMentionEmailId(id: string) {
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
}

/**
 * Parses a single mention from its components.
 *
 * @param name - The display name from the markdown link
 * @param encodedData - The data after 'mention:' in the URL
 * @param fullMatch - The full matched string for error reporting
 * @returns Parsed mention or null if invalid
 */
function parseSingleMention(
  name: string,
  encodedData: string,
  fullMatch: string
): ParsedMention | null {
  if (encodedData === ALINE_AI_MENTION_PATH) return null;

  /**
   * Split by ? to separate path from query params
   * Example: 'document/doc-123?version=v1' => ['document/doc-123', 'version=v1']
   */
  const [pathPart] = encodedData.split('?');
  if (pathPart?.startsWith(REPORT_CELL_MENTION_PATH_PREFIX)) return null;
  if (!pathPart) {
    console.warn(`Invalid mention format - empty path: ${fullMatch}.`);
    return null;
  }

  const parsed = parsePathPart(pathPart);
  if (!parsed) {
    console.warn(`Invalid mention format - could not parse path: ${fullMatch}.`);
    return null;
  }

  const { id, type } = parsed;
  if (!id.trim() || !type.trim()) {
    console.warn(`Invalid mention format - empty fields: ${fullMatch}.`);
    return null;
  }

  return {
    id,
    name,
    type,
  };
}

/**
 * Extracts file mentions from markdown text.
 *
 * Looks for links in the format: [@fileName](mention:mentionTypeName/fileId)
 * or [fileName](mention:mentionTypeName/fileId)
 * with optional query parameters for metadata
 *
 * @param markdown - The markdown text to parse
 * @returns Array of parsed mentions with fileId, fileName, and mentionTypeName
 *
 * @example
 * ```ts
 * const markdown = "Please review [@Contract.pdf](mention:document/abc-123) and [NDA.pdf](mention:document/xyz-789)";
 * const mentions = parseMarkdownMentions(markdown);
 * // Returns: [
 * //   { fileId: 'abc-123', fileName: 'Contract.pdf', mentionTypeName: 'document' },
 * //   { fileId: 'xyz-789', fileName: 'NDA.pdf', mentionTypeName: 'document' }
 * // ]
 * ```
 */
export function parseMarkdownMentions(markdown: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];

  /**
   * Forward scan to keep parsing O(n) and ReDoS-free, but with a depth
   * counter so brackets nested inside the link's display name (e.g.
   * "[@Contract (v2) [final].pdf](mention:document/doc-123)") don't
   * confuse the matcher — `lastIndexOf('[')` would grab the inner '['
   * and emit a wrong slice.
   */
  let pos = 0;
  while (pos < markdown.length) {
    const startIdx = markdown.indexOf('[', pos);
    if (startIdx === -1) break;

    /**
     * Walk forward from the '[' tracking nesting depth to find the
     * matching outer ']'. depth=0 after counting the opening bracket =
     * we've closed the outer pair.
     */
    let depth = 0;
    let nameEndIdx = -1;
    for (let i = startIdx; i < markdown.length; i++) {
      const ch = markdown[i];
      if (ch === '[') depth += 1;
      else if (ch === ']') {
        depth -= 1;
        if (depth === 0) {
          nameEndIdx = i;
          break;
        }
      }
    }
    if (nameEndIdx === -1) {
      // Unclosed `[` — skip past it and keep scanning.
      pos = startIdx + 1;
      continue;
    }

    /**
     * Outer ']' must be immediately followed by '(mention:' for this to
     * be a mention link.
     */
    const linkPrefix = '(mention:';
    if (markdown.slice(nameEndIdx + 1, nameEndIdx + 1 + linkPrefix.length) !== linkPrefix) {
      pos = nameEndIdx + 1;
      continue;
    }

    const dataStart = nameEndIdx + 1 + linkPrefix.length;
    const closeIdx = markdown.indexOf(')', dataStart);
    if (closeIdx === -1) {
      pos = nameEndIdx + 1;
      continue;
    }

    const nameStart = markdown[startIdx + 1] === '@' ? startIdx + 2 : startIdx + 1;
    const name = markdown.slice(nameStart, nameEndIdx);
    const encodedData = markdown.slice(dataStart, closeIdx);
    const fullMatch = markdown.slice(startIdx, closeIdx + 1);

    const parsedMention = parseSingleMention(name, encodedData, fullMatch);
    if (parsedMention) mentions.push(parsedMention);

    pos = closeIdx + 1;
  }

  return mentions;
}

/**
 * Extracts unique document and report IDs from markdown mentions.
 * Includes both direct document/report mentions and document IDs from selection mentions.
 *
 * @param markdown - The markdown text to parse
 * @returns Array of unique document and report IDs mentioned in the text
 */
type FileIdMentionType = DocumentBackedMentionType | 'report';

export function extractFileIdsFromMarkdown(
  markdown: string
): { id: string; name: string; type: FileIdMentionType }[] {
  const mentions = parseMarkdownMentions(markdown);
  const documentsAndReports = mentions
    .filter(getIsValidDocumentMention)
    .map(m => ({ id: m.id, name: m.name, type: m.type }));

  return uniqBy(documentsAndReports, 'id');
}

function getIsValidDocumentMention(
  mention: ParsedMention
): mention is ParsedMention & { type: FileIdMentionType } {
  const isDocumentMention =
    mention.type === 'report' || getIsDocumentBackedMentionType(mention.type);
  return isDocumentMention && DOCUMENT_MENTION_ID_SCHEMA.safeParse(mention.id).success;
}
